import { jsx, jsxs } from "react/jsx-runtime";
import { Tooltip } from "@base-ui/react/tooltip";
import "react";
import { usePortalContainer } from "../../contexts/index.js";
import { tv } from "../../variants/tv.js";
import { CdnIcon } from "../CdnIcon/index.js";
const tooltipStyles = tv({
    slots: {
        baseSlot: 'rounded-xl px-3 py-2 text-body3 transition-all',
        iconSlot: 'mr-1',
        arrowSlot: 'h-(--tooltip-arrow-size) w-(--tooltip-arrow-size) rotate-45 rounded-tl-[3px] [clip-path:polygon(0%_0%,0%_100%,100%_0%)]',
        arrowContainerSlot: 'data-[side=bottom]:top-(--tooltip-arrow-offset-y) data-[side=left]:right-(--tooltip-arrow-offset-x) data-[side=left]:rotate-90 data-[side=right]:left-(--tooltip-arrow-offset-x) data-[side=right]:-rotate-90 data-[side=top]:bottom-(--tooltip-arrow-offset-y) data-[side=top]:rotate-180'
    },
    variants: {
        color: {
            neutral: {},
            success: {},
            error: {}
        },
        emphasis: {
            true: {},
            false: {}
        },
        showArrow: {
            true: {},
            false: {}
        },
        fixedWidth: {
            true: {},
            false: {}
        }
    },
    compoundSlots: [
        {
            slots: [
                'baseSlot',
                'arrowSlot'
            ],
            class: 'shadow-lg'
        },
        {
            slots: [
                'baseSlot'
            ],
            fixedWidth: false,
            class: 'w-auto'
        },
        {
            slots: [
                'baseSlot'
            ],
            fixedWidth: true,
            class: 'w-44'
        },
        {
            slots: [
                'arrowSlot'
            ],
            showArrow: false,
            class: 'invisible'
        },
        {
            slots: [
                'baseSlot',
                'arrowSlot'
            ],
            emphasis: false,
            class: 'bg-neutral-1'
        },
        {
            slots: [
                'baseSlot'
            ],
            emphasis: false,
            color: 'neutral',
            class: 'text-neutral-11'
        },
        {
            slots: [
                'baseSlot'
            ],
            emphasis: false,
            color: 'success',
            class: 'text-success-11'
        },
        {
            slots: [
                'baseSlot'
            ],
            emphasis: false,
            color: 'error',
            class: 'text-error-11'
        },
        {
            slots: [
                'baseSlot',
                'arrowSlot'
            ],
            emphasis: true,
            color: 'neutral',
            class: 'bg-neutral-8 text-neutral-1'
        },
        {
            slots: [
                'baseSlot',
                'arrowSlot'
            ],
            emphasis: true,
            color: 'success',
            class: 'bg-success-8 text-neutral-1'
        },
        {
            slots: [
                'baseSlot',
                'arrowSlot'
            ],
            emphasis: true,
            color: 'error',
            class: 'bg-error-8 text-neutral-1'
        },
        {
            slots: [
                'iconSlot'
            ],
            emphasis: false,
            color: 'neutral',
            class: 'text-neutral-10'
        },
        {
            slots: [
                'iconSlot'
            ],
            emphasis: false,
            color: 'success',
            class: 'text-success-8'
        },
        {
            slots: [
                'iconSlot'
            ],
            emphasis: false,
            color: 'error',
            class: 'text-error-8'
        },
        {
            slots: [
                'iconSlot'
            ],
            emphasis: true,
            color: 'neutral',
            class: 'text-neutral-4'
        },
        {
            slots: [
                'iconSlot'
            ],
            emphasis: true,
            color: 'success',
            class: 'text-success-7'
        },
        {
            slots: [
                'iconSlot'
            ],
            emphasis: true,
            color: 'error',
            class: 'text-error-7'
        },
        {
            slots: [
                'baseSlot'
            ],
            class: 'transition-duration-extra-short transition-duration-snap motion-safe:data-[ending-style]:animate-fade-out-100-0 motion-safe:data-[starting-style]:animate-fade-in-0-100'
        }
    ]
});
const ValidTooltipColorValues = Object.keys(tooltipStyles.variants.color);
function Tooltip_Tooltip(props) {
    const { children, triggerNode, className, iconLeft, color = 'neutral', align = 'center', side = 'bottom', showArrow = true, alignOffset = 0, sideOffset = 10, fixedWidth = false, width = 'auto', emphasis = false, delay = 600, closeDelay = 0, hoverable = true, open, defaultOpen = false, onOpenChange } = props;
    const portalContainer = usePortalContainer();
    const { baseSlot, iconSlot, arrowContainerSlot, arrowSlot } = tooltipStyles({
        color,
        emphasis,
        showArrow,
        fixedWidth: fixedWidth || 'fixed' === width
    });
    return /*#__PURE__*/ jsx(Tooltip.Provider, {
        delay: delay,
        closeDelay: closeDelay,
        children: /*#__PURE__*/ jsxs(Tooltip.Root, {
            disableHoverablePopup: !hoverable,
            defaultOpen: defaultOpen,
            open: open,
            onOpenChange: onOpenChange,
            children: [
                /*#__PURE__*/ jsx(Tooltip.Trigger, {
                    className: className,
                    render: /*#__PURE__*/ jsx("div", {}),
                    children: triggerNode
                }),
                /*#__PURE__*/ jsx(Tooltip.Portal, {
                    container: portalContainer || void 0,
                    children: /*#__PURE__*/ jsx(Tooltip.Positioner, {
                        align: align,
                        alignOffset: alignOffset,
                        side: side,
                        sideOffset: sideOffset,
                        arrowPadding: 16,
                        children: /*#__PURE__*/ jsxs(Tooltip.Popup, {
                            className: baseSlot(),
                            children: [
                                /*#__PURE__*/ jsx(Tooltip.Arrow, {
                                    className: arrowContainerSlot(),
                                    children: /*#__PURE__*/ jsx("div", {
                                        className: arrowSlot(),
                                        "data-testid": "tooltip-arrow"
                                    })
                                }),
                                /*#__PURE__*/ jsxs("div", {
                                    className: "flex flex-row items-start justify-center",
                                    children: [
                                        iconLeft && /*#__PURE__*/ jsx(CdnIcon, {
                                            iconName: iconLeft,
                                            className: iconSlot(),
                                            size: "16"
                                        }),
                                        children
                                    ]
                                })
                            ]
                        })
                    })
                })
            ]
        })
    });
}
export { Tooltip_Tooltip as Tooltip, ValidTooltipColorValues };
